import React, { useState } from "react";
import { FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useParams, useNavigate } from "react-router-dom";
import styles from "./Auth.module.css";
// import AadviLogo from "../assets/Aadvi-logo.png";
import { MAIL_URL } from "../api";
import { toast } from "react-hot-toast";

export default function ResetPassword() {
    // Get token from URL params
    const { token } = useParams();
    const navigate = useNavigate();
    // Form state
    const [form, setForm] = useState({
        password: "",
        confirmPassword: "",
    });
    // Error state
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState({ password: false, confirmPassword: false });

    // Validation logic for fields
    const validateField = (name, value) => {
        switch (name) {
            case "password":
                if (!value) return "Please fill the New Password";
                // Regex from Signup page
                if (!/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(value)) {
                    return "Password must contain min 6 chars, at least 1 number & 1 special character";
                }
                break;
            case "confirmPassword":
                if (!value) return "Please fill the Confirm Password";
                if (value !== form.password) return "Passwords do not match";
                break;
            default:
                break;
        }
        return "";
    };

    // Validate all fields on submit
    const validateAll = () => {
        const newErrors = {};
        Object.keys(form).forEach((key) => {
            const error = validateField(key, form[key]);
            if (error) newErrors[key] = error;
        });
        return newErrors;
    };
    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
        const error = validateField(name, value);
        setErrors({ ...errors, [name]: error });
    };
    // Handle focus and blur events
    const handleFocus = (field) => setIsFocused(prev => ({ ...prev, [field]: true }));
    const handleBlur = (field) => setIsFocused(prev => ({ ...prev, [field]: false }));

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateAll();
        setErrors(validationErrors);

        // If there are validation errors, show a toast and prevent submission
        if (Object.keys(validationErrors).length > 0) {
            toast.error("Please correct the errors in the form.", { position: "top-center" });
            return;
        }
        // Submit new password to API
        setIsLoading(true);
        try {
            // Make API call to reset password
            const res = await fetch(`${MAIL_URL}/api/users/reset-password/${token}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    password: form.password,
                    confirmPassword: form.confirmPassword,
                }),
            });
            // Parse response
            const data = await res.json();

            // Handle non OK responses
            if (!res.ok) {
                toast.error(data.message || "Password reset failed", {
                    position: "top-center",
                });
                return;
            }
            // Success
            toast.success("Password reset successful", { position: "top-center" });
            navigate("/login");
        } catch (err) {
            toast.error(err.message, { position: "top-center" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.authContainer}>
            {/* Boutique Style Background Elements */}
            <div className={styles.boutiqueBackground}>
                <div className={styles.luxuryPattern}></div>
                <div className={styles.crystalShape1}></div>
                <div className={styles.crystalShape2}></div>
                <div className={styles.crystalShape3}></div>
                <div className={styles.goldAccent1}></div>
                <div className={styles.goldAccent2}></div>
            </div>

            {/* Enhanced Floating Shapes */}
            <div className={styles.floatingShapes}>
                <div className={styles.shape1}></div>
                <div className={styles.shape2}></div>
                <div className={styles.shape3}></div>
                <div className={styles.shape4}></div>
            </div>

            {/* Elegant Particles */}
            <div className={styles.particles}>
                {[...Array(12)].map((_, i) => (
                    <div key={i} className={styles.particle} style={{
                        '--delay': `${i * 0.3}s`,
                        '--duration': `${12 + i * 1.5}s`,
                        left: `${Math.random() * 100}%`
                    }}></div>
                ))}
            </div>

            <div className={`card ${styles.authCard}`}>
                <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Set New Password</h3>
                </div>

                <div className="card-body p-4 p-md-5 p-lg-5">
                    <form onSubmit={handleSubmit}>

                        {/* New Password Field */}
                        <div className={`mb-4 ${styles.inputContainer}`}>
                            <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.password || isFocused.password) ? styles.labelFloating : ''}`}>
                                New Password
                            </label>
                            <div className={`input-group ${styles.inputGroup}`}>
                                <span className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}>
                                    <FaLock />
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className={`form-control ${styles.formControl}`}
                                    name="password"
                                    value={form.password}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('password')}
                                    onBlur={() => handleBlur('password')}
                                    placeholder=" "
                                />
                                <button
                                    className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                                <div className={styles.inputUnderline}></div>
                            </div>
                            {errors.password && <div className={`text-danger ${styles.errorText}`}>{errors.password}</div>}
                        </div>

                        {/* Confirm Password Field */}
                        <div className={`mb-4 ${styles.inputContainer}`}>
                            <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.confirmPassword || isFocused.confirmPassword) ? styles.labelFloating : ''}`}>
                                Confirm Password
                            </label>
                            <div className={`input-group ${styles.inputGroup}`}>
                                <span className={`input-group-text ${styles.inputIcon} ${isFocused.confirmPassword ? styles.inputIconFocused : ''}`}>
                                    <FaLock />
                                </span>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    className={`form-control ${styles.formControl}`}
                                    name="confirmPassword"
                                    value={form.confirmPassword}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('confirmPassword')}
                                    onBlur={() => handleBlur('confirmPassword')}
                                    placeholder=" "
                                />
                                <button
                                    className={`input-group-text ${styles.inputIcon} ${isFocused.confirmPassword ? styles.inputIconFocused : ''}`}
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                                <div className={styles.inputUnderline}></div>
                            </div>
                            {errors.confirmPassword && <div className={`text-danger ${styles.errorText}`}>{errors.confirmPassword}</div>}
                        </div>

                        <div className="d-grid mb-4">
                            <button
                                type="submit"
                                className={`${styles.btn} ${isLoading ? styles.btnLoading : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        <span className={styles.btnText}>Resetting...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className={styles.btnText}>Reset Password</span>
                                        <div className={styles.btnShine}></div>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="text-center mt-3">
                        <Link to="/login" className={styles.authLink}>
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}